import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  Container,
  Grid,
  Loader,
  Modal,
  Tabs,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBrandProducthunt,
  IconCheck,
  IconPlus,
  IconSpeakerphone,
  IconX,
} from "@tabler/icons-react";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { Product, ProductInput } from "../../../gql/__generated__/graphql";
import {
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
} from "../../apis/lambda/GraphQL/Product/mutations";
import { GET_ALL_PRODUCTS_FOR_BRAND } from "../../apis/lambda/GraphQL/Product/queries";
import { userInfoAtom } from "../../atoms";
import ProductCard from "../../components/Cards/ProductCard";
import FilterPanel, {
  FilterOption,
  SortOption,
} from "../../components/Filter/FilterPanel";
import { PRODUCT_CATEGORIES } from "../../constants/webData";
import {
  Campaign,
  CampaignFormValues,
  CampaignStatus,
} from "./Campaigns/Campaign.types";
import CampaignForm from "./Campaigns/CampaignForm";
import CampaignItem from "./Campaigns/components/CampaignItem";
import ProductForm, { ProductFormValues } from "./components/ProductForm";

// TODO: Implement actual campaign creation logic
const createCampaign = async (
  campaignData: CampaignFormValues,
  allProducts: Product[]
): Promise<Campaign> => {
  console.error("createCampaign is not implemented", campaignData);
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1500));

  // This is mock data. Replace with actual API call.
  return {
    id: Date.now().toString(),
    name: campaignData.name,
    brandId: "brand-123", // Would come from auth context in real app
    products: allProducts.filter((p) => campaignData.products.includes(p.id)),
    requirements: {
      videosDesired: campaignData.videosDesired,
      affiliateGMVMinimum: campaignData.affiliateGMVMinimum,
    },
    suggestedBudget: campaignData.suggestedBudget || 0,
    actualBudget: campaignData.actualBudget || 0,
    status: CampaignStatus.Active,
    createdAt: new Date(),
    updatedAt: new Date(),
    collabsCreated: 0,
    collabsAccepted: 0,
    collabsCompleted: 0,
  };
};

function ProductsPage() {
  // const renderCount = useRef(0);
  // renderCount.current += 1;
  // console.log(`ProducsPage render #${renderCount.current}`);

  const [activeTab, setActiveTab] = useState<string | null>("products");
  // const [products, setProducts] = useState<Product[]>(initialProducts);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [modalOpened, setModalOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("name");
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Jotai State
  const userInfo = useAtomValue(userInfoAtom);

  // GraphQL
  const productsResponse = useQuery(GET_ALL_PRODUCTS_FOR_BRAND, {
    variables: {
      brandId: userInfo?.brandId!,
    },
  });
  // Mutations
  const [createProduct, createProductResponse] = useMutation(CREATE_PRODUCT);
  const [updateProduct, updateProductResponse] = useMutation(UPDATE_PRODUCT);

  const products = productsResponse.data?.getAllProductsForBrand || [];

  useEffect(() => {
    // Initialize filtered products
    setFilteredProducts(products);
  }, [products]);
  // useEffect(() => {
  //   // Initialize filtered products
  //   setFilteredProducts(productsResponse.data?.getAllProductsForBrand || []);
  // }, [productsResponse]);

  const handleAddProduct = async (values: ProductFormValues) => {
    try {
      // Ensure price is stored as a number
      const parsedPrice = parseFloat(values.price.toString()) || 0;

      const newProduct: ProductInput = {
        name: values.name,
        imageUrl: values.imageUrl,
        description: values.description,
        price: parsedPrice,
        categories: values.categories,
        productUrl: values.productUrl,
        brandId: userInfo?.brandId!,
      };

      await createProduct({
        variables: {
          product: newProduct,
        },
        refetchQueries: [
          {
            query: GET_ALL_PRODUCTS_FOR_BRAND,
            variables: { brandId: userInfo?.brandId },
          },
        ],
      });

      notifications.show({
        title: "Success!",
        message: `Product "${values.name}" has been created successfully.`,
        color: "secondary",
        icon: <IconCheck size={16} />,
        autoClose: 5000,
      });

      setModalOpened(false);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to create product. Please try again.",
        color: "red",
        icon: <IconX size={16} />,
        autoClose: 5000,
      });
      console.error("Error creating product:", error);
    }
  };

  const handleEditProduct = async (values: ProductFormValues) => {
    if (!editingProduct) return;

    try {
      // Ensure price is stored as a number
      const parsedPrice = parseFloat(values.price.toString()) || 0;

      const updatedProductInput: ProductInput = {
        name: values.name,
        imageUrl: values.imageUrl,
        description: values.description,
        price: parsedPrice,
        categories: values.categories,
        productUrl: values.productUrl,
        brandId: userInfo?.brandId!,
      };

      await updateProduct({
        variables: {
          id: editingProduct.id,
          product: updatedProductInput,
        },
        refetchQueries: [
          {
            query: GET_ALL_PRODUCTS_FOR_BRAND,
            variables: { brandId: userInfo?.brandId },
          },
        ],
      });

      notifications.show({
        title: "Success!",
        message: `Product "${values.name}" has been updated successfully.`,
        color: "secondary",
        icon: <IconCheck size={16} />,
        autoClose: 5000,
      });

      setEditingProduct(null);
      setModalOpened(false);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to update product. Please try again.",
        color: "red",
        icon: <IconX size={16} />,
        autoClose: 5000,
      });
      console.error("Error updating product:", error);
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setEditingProduct(null);
    setModalOpened(false);
  };

  const handleStartCampaign = async (values: CampaignFormValues) => {
    setIsLoading(true);
    try {
      const newCampaign = await createCampaign(values, products as Product[]);
      setCampaigns([...campaigns, newCampaign]);

      notifications.show({
        title: "Campaign Created!",
        message: `Campaign "${values.name}" has been started successfully.`,
        color: "secondary",
        icon: <IconCheck size={16} />,
        autoClose: 5000,
      });

      setModalOpened(false);
      setActiveTab("campaigns");
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to create campaign. Please try again.",
        color: "red",
        icon: <IconX size={16} />,
        autoClose: 5000,
      });
      console.error("Error creating campaign:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setSortBy("name");
  };

  const filterOptions: FilterOption[] = [
    {
      id: "search",
      label: "Search",
      type: "search",
      placeholder: "Search products...",
      value: searchQuery,
      onChange: setSearchQuery,
      searchFields: ["name", "description"],
    },
    {
      id: "categories",
      label: "Categories",
      type: "multiSelect",
      placeholder: "Filter by category",
      data: PRODUCT_CATEGORIES.map((cat) => ({ value: cat, label: cat })),
      value: selectedCategories,
      onChange: setSelectedCategories,
      filterFn: (product: Product, selectedCats: string[]) => {
        if (selectedCats.length === 0) return true;
        return product.categories.some((category) =>
          selectedCats.includes(category)
        );
      },
    },
  ];

  const sortOptions: SortOption[] = [
    {
      value: "name",
      label: "Name (A-Z)",
      sortFn: (a: Product, b: Product) => a.name.localeCompare(b.name),
    },
    {
      value: "price-asc",
      label: "Price (Low to High)",
      sortFn: (a: Product, b: Product) => a.price - b.price,
    },
    {
      value: "price-desc",
      label: "Price (High to Low)",
      sortFn: (a: Product, b: Product) => b.price - a.price,
    },
    {
      value: "newest",
      label: "Newest First",
      sortFn: (a: Product, b: Product) => parseInt(b.id) - parseInt(a.id),
    },
  ];

  return (
    <Container size="xl" py={"md"}>
      {activeTab === "products" ? (
        <Button
          size="md"
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setEditingProduct(null);
            setModalOpened(true);
          }}
        >
          Add Product
        </Button>
      ) : (
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setEditingProduct(null);
            setModalOpened(true);
          }}
          disabled={products.length === 0}
          variant="gradient"
          gradient={{ from: "primary", to: "secondary", deg: 45 }}
          fw={600}
          size="md"
          style={{
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            transform: "translateY(0)",
            transition: "transform 0.2s, box-shadow 0.2s",
            ":hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          Start a Campaign
        </Button>
      )}

      <Tabs
        mt={"md"}
        value={activeTab}
        onChange={setActiveTab}
        variant="outline"
      >
        <Tabs.List
          grow
          mb="md"
          style={{ borderBottom: "2px solid var(--mantine-color-gray-3)" }}
        >
          <Tabs.Tab
            value="products"
            leftSection={<IconBrandProducthunt size={20} />}
            fz="lg"
            py="md"
          >
            Products
          </Tabs.Tab>
          <Tabs.Tab
            value="campaigns"
            leftSection={<IconSpeakerphone size={20} />}
            fz="lg"
            py="md"
          >
            Campaigns
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="products">
          <FilterPanel
            filterOptions={filterOptions}
            sortOptions={sortOptions}
            sortValue={sortBy}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
            data={products}
            onFilteredDataChange={setFilteredProducts}
          />

          {productsResponse.loading ? (
            <Box className="center" mt={"lg"}>
              <Loader />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Center p="xl">
              <Text ta="center" fz="lg" c="dimmed">
                No products found. Try adjusting your search or add a new
                product.
              </Text>
            </Center>
          ) : (
            <Grid mt="md">
              {filteredProducts.map((product) => (
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={product.id}>
                  <ProductCard product={product} onEdit={openEditModal} />
                </Grid.Col>
              ))}
            </Grid>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="campaigns">
          {campaigns.length === 0 ? (
            <Center p="xl">
              <Text ta="center" fz="lg" c="dimmed">
                No campaigns yet. Start a campaign to reach creators.
              </Text>
            </Center>
          ) : (
            <Grid mt="md">
              {campaigns.map((campaign) => (
                <Grid.Col span={12} key={campaign.id}>
                  <CampaignItem campaign={campaign} />
                </Grid.Col>
              ))}
            </Grid>
          )}
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={modalOpened}
        onClose={handleCloseModal}
        title={
          activeTab === "products"
            ? editingProduct
              ? "Edit Product"
              : "Add New Product"
            : "Start a Campaign"
        }
        size={activeTab === "products" ? "lg" : "xl"}
      >
        {activeTab === "products" ? (
          <ProductForm
            initialValues={editingProduct || undefined}
            onSubmit={editingProduct ? handleEditProduct : handleAddProduct}
            onCancel={handleCloseModal}
            submitLabel={editingProduct ? "Save Changes" : "Add Product"}
            isLoading={
              editingProduct
                ? updateProductResponse.loading
                : createProductResponse.loading
            }
          />
        ) : (
          <CampaignForm
            products={products}
            onSubmit={handleStartCampaign}
            onCancel={handleCloseModal}
          />
        )}
      </Modal>
    </Container>
  );
}

export default ProductsPage;
